import * as React from 'react';
import { Search, RotateCcw, Regex, HelpCircle, X } from 'lucide-react';
import { cn } from '../lib/utils.js';
import type { LogLevel, SearchParams } from '../lib/api.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { MultiSelect } from './ui/select.js';

const LOG_LEVEL_OPTIONS: Array<{ value: LogLevel; label: string }> = [
  { value: 'debug', label: 'Debug' },
  { value: 'info', label: 'Info' },
  { value: 'warn', label: 'Warn' },
  { value: 'error', label: 'Error' },
  { value: 'fatal', label: 'Fatal' },
];

export interface SearchBarProps {
  /** Current search parameters */
  value?: Partial<SearchParams>;
  /** Callback when search is submitted. Params may not include from/to if user didn't enter them. */
  onSearch: (params: Partial<SearchParams>) => void;
  /** Callback when search is reset */
  onReset?: () => void;
  /** Whether search is in progress */
  isLoading?: boolean;
  /** Additional class name */
  className?: string;
  /** Default date range in hours (default: 24) */
  defaultRangeHours?: number;
}

/**
 * Format date to datetime-local format (YYYY-MM-DDTHH:mm)
 */
function formatToDatetimeLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

/**
 * Convert ISO string or datetime-local string to datetime-local format
 */
function toDatetimeLocal(dateStr: string): string {
  // If already in datetime-local format (no Z or timezone), return as-is
  if (!dateStr.includes('Z') && !dateStr.includes('+')) {
    return dateStr;
  }
  // Convert ISO string to datetime-local
  const date = new Date(dateStr);
  return formatToDatetimeLocal(date);
}

/**
 * SearchBar provides filtering options for log queries.
 */
export function SearchBar({
  value,
  onSearch,
  onReset,
  isLoading = false,
  className,
  defaultRangeHours = 24,
}: SearchBarProps) {
  // Only prefill dates if explicitly provided via value prop (e.g., from URL)
  const [from, setFrom] = React.useState(value?.from ? toDatetimeLocal(value.from) : '');
  const [to, setTo] = React.useState(value?.to ? toDatetimeLocal(value.to) : '');
  const [levels, setLevels] = React.useState<LogLevel[]>(value?.levels ?? []);
  const [subApp, setSubApp] = React.useState(value?.sub_apps?.[0] ?? '');
  const [instanceId, setInstanceId] = React.useState(value?.instance_ids?.[0] ?? '');
  const [sessionId, setSessionId] = React.useState(value?.session_ids?.[0] ?? '');
  const [correlationId, setCorrelationId] = React.useState(value?.correlation_ids?.[0] ?? '');
  const [envExclude, setEnvExclude] = React.useState(value?.idx1_exclude?.join(', ') ?? '');
  const [message, setMessage] = React.useState(value?.match ?? '');
  const [useRegex, setUseRegex] = React.useState(!!value?.regex);
  const [showHelp, setShowHelp] = React.useState(false);

  // Update internal state when value prop changes
  React.useEffect(() => {
    // Only update from/to if explicitly provided, otherwise leave empty
    if (value?.from) setFrom(toDatetimeLocal(value.from));
    if (value?.to) setTo(toDatetimeLocal(value.to));
    if (value?.levels) setLevels(value.levels);
    if (value?.sub_apps?.[0]) setSubApp(value.sub_apps[0]);
    if (value?.instance_ids?.[0] !== undefined) setInstanceId(value.instance_ids[0]);
    if (value?.session_ids?.[0] !== undefined) setSessionId(value.session_ids[0]);
    if (value?.correlation_ids?.[0] !== undefined) setCorrelationId(value.correlation_ids[0]);
    if (value?.idx1_exclude) setEnvExclude(value.idx1_exclude.join(', '));
    if (value?.match) setMessage(value.match);
    if (value?.regex) {
      setMessage(value.regex);
      setUseRegex(true);
    }
  }, [value]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Convert datetime-local to ISO string
    const toISOString = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toISOString();
    };

    // Only include from/to if user explicitly entered them
    // This allows LogViewer to compute defaults for API but not persist to URL
    const params: Partial<SearchParams> = {};

    if (from) {
      params.from = toISOString(from);
    }
    if (to) {
      params.to = toISOString(to);
    }

    if (levels.length > 0) {
      params.levels = levels;
    }

    if (subApp.trim()) {
      params.sub_apps = [subApp.trim()];
    }

    if (instanceId.trim()) {
      params.instance_ids = [instanceId.trim()];
    }

    if (sessionId.trim()) {
      params.session_ids = [sessionId.trim()];
    }

    if (correlationId.trim()) {
      params.correlation_ids = [correlationId.trim()];
    }

    // Parse environment exclude (comma-separated)
    if (envExclude.trim()) {
      const excludeValues = envExclude.split(',').map(v => v.trim()).filter(v => v);
      if (excludeValues.length > 0) {
        params.idx1_exclude = excludeValues;
      }
    }

    if (message.trim()) {
      if (useRegex) {
        params.regex = message.trim();
      } else {
        params.match = message.trim();
      }
    }

    onSearch(params);
  };

  const handleReset = () => {
    setFrom('');
    setTo('');
    setLevels([]);
    setSubApp('');
    setInstanceId('');
    setSessionId('');
    setCorrelationId('');
    setEnvExclude('');
    setMessage('');
    setUseRegex(false);
    onReset?.();
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'space-y-4 rounded-xl p-5',
        'border border-[var(--color-border)] bg-[var(--color-card)]',
        'shadow-[0_1px_3px_rgba(0,0,0,0.1)]',
        className
      )}
    >
      <div className="flex flex-wrap gap-4">
        {/* Date Range */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="from" className="text-xs font-medium text-[var(--color-muted-foreground)]">
            From
          </label>
          <Input
            id="from"
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-48"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="to" className="text-xs font-medium text-[var(--color-muted-foreground)]">
            To
          </label>
          <Input
            id="to"
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-48"
          />
        </div>

        {/* Level Filter */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-[var(--color-muted-foreground)]">
            Levels
          </label>
          <MultiSelect
            options={LOG_LEVEL_OPTIONS}
            value={levels}
            onChange={(values) => setLevels(values as LogLevel[])}
            placeholder="All levels"
            className="w-40"
          />
        </div>

        {/* Sub App Filter */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="subApp" className="text-xs font-medium text-[var(--color-muted-foreground)]">
            Sub App
          </label>
          <Input
            id="subApp"
            type="text"
            value={subApp}
            onChange={(e) => setSubApp(e.target.value)}
            placeholder="Filter by sub app"
            className="w-40"
          />
        </div>

        {/* Instance ID Filter */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="instanceId" className="text-xs font-medium text-[var(--color-muted-foreground)]">
            Instance ID
          </label>
          <Input
            id="instanceId"
            type="text"
            value={instanceId}
            onChange={(e) => setInstanceId(e.target.value)}
            placeholder="Filter by instance"
            className="w-40"
          />
        </div>

        {/* Session ID Filter */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="sessionId" className="text-xs font-medium text-[var(--color-muted-foreground)]">
            Session ID
          </label>
          <Input
            id="sessionId"
            type="text"
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
            placeholder="Filter by session"
            className="w-48"
          />
        </div>

        {/* Correlation ID Filter */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="correlationId" className="text-xs font-medium text-[var(--color-muted-foreground)]">
            Correlation ID
          </label>
          <Input
            id="correlationId"
            type="text"
            value={correlationId}
            onChange={(e) => setCorrelationId(e.target.value)}
            placeholder="Filter by request"
            className="w-48"
          />
        </div>

        {/* Environment Exclude Filter (uses idx1) */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="envExclude" className="text-xs font-medium text-[var(--color-muted-foreground)]">
            Exclude Env
          </label>
          <Input
            id="envExclude"
            type="text"
            value={envExclude}
            onChange={(e) => setEnvExclude(e.target.value)}
            placeholder="dev, staging"
            className="w-32"
          />
        </div>
      </div>

      {/* Message Search */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-muted-foreground)]" />
          <Input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={useRegex ? 'Regex pattern...' : 'Search message...'}
            className="pl-10"
          />
        </div>
        <Button
          type="button"
          variant={useRegex ? 'default' : 'outline'}
          size="icon"
          onClick={() => setUseRegex(!useRegex)}
          title={useRegex ? 'Using regex' : 'Use regex'}
        >
          <Regex className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handleReset}
          title="Reset filters"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
        <div className="relative">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setShowHelp(!showHelp)}
            title="Search help"
          >
            <HelpCircle className="h-4 w-4" />
          </Button>
          {showHelp && (
            <div className="absolute right-0 top-full mt-2 z-50 w-80 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4 shadow-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-sm text-[var(--color-foreground)]">Search Help</h4>
                <button
                  type="button"
                  onClick={() => setShowHelp(false)}
                  className="text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-3 text-xs text-[var(--color-muted-foreground)]">
                <div>
                  <h5 className="font-medium text-[var(--color-foreground)] mb-1">Date Range</h5>
                  <p>Leave empty to search all logs (last 7 days default). Set specific dates to narrow your search.</p>
                </div>
                <div>
                  <h5 className="font-medium text-[var(--color-foreground)] mb-1">Message Search</h5>
                  <p>Type text to search log messages. Use the <span className="inline-flex items-center px-1 py-0.5 rounded bg-[var(--color-secondary)]"><Regex className="h-3 w-3" /></span> button to enable regex patterns.</p>
                </div>
                <div>
                  <h5 className="font-medium text-[var(--color-foreground)] mb-1">Regex Examples</h5>
                  <ul className="space-y-1 font-mono text-[10px]">
                    <li><code className="bg-[var(--color-secondary)] px-1 rounded">error|warn</code> - match error OR warn</li>
                    <li><code className="bg-[var(--color-secondary)] px-1 rounded">user.*login</code> - user followed by login</li>
                    <li><code className="bg-[var(--color-secondary)] px-1 rounded">GET /api/\d+</code> - GET /api/ with number</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-medium text-[var(--color-foreground)] mb-1">IDs</h5>
                  <p><strong>Session ID</strong> - tracks user session across requests<br/>
                  <strong>Correlation ID</strong> - tracks single request across services</p>
                </div>
                <div className="pt-2 border-t border-[var(--color-border)]">
                  <p className="text-[10px]">Tip: Click badges in log entries to filter by that value.</p>
                </div>
              </div>
            </div>
          )}
        </div>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              Searching...
            </>
          ) : (
            <>
              <Search className="h-4 w-4" />
              Search
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

export default SearchBar;
